import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Check,
  ExternalLink,
  FileText,
  Folder,
  Info,
  List,
  ListOrdered,
  Lock,
  Link2,
  Mail,
  Newspaper,
  Pencil,
  Plus,
  RemoveFormatting,
  RotateCcw,
  Send,
  Sparkles,
  Star,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useEtatGlobal } from "../../pages/EtatGlobal";
import { useClients } from "../../pages/Portefeuille/useClients.js";
import { dialog } from "../ui/Dialog";
import { RV_DEMO } from "./rdvDemo";
import { analyserInsights, chercherProspects, enregistrerNote, envoyerMail, estTesteur, synthetiser } from "./rdvApi";
import "./RdvHud.css";

// Rendez-vous assisté par IA — interface HUD sombre ouverte depuis EVA
// (maquette Claude Design : claude-design/rdv-ai.jsx).
// Pour les testeurs (rdvApi.js) : dictée du navigateur, insights en direct
// (règles avec sources officielles, actualité, dossier, questions — backend
// services/rdvInsights.js), synthèse et mail par l'IA, enregistrement dans la
// note de RDV du client ou du prospect, envoi du mail depuis leur Gmail.
// Pour tous : la démo rejoue un RDV fictif entièrement côté écran.

const RV_SPK = { cpt: "Comptable", cli: "Client" };
const RV_TYPES = {
  regle: { lab: "Règle & seuils", Ico: BookOpen },
  dossier: { lab: "Dossier client", Ico: Folder },
  question: { lab: "À demander", Ico: Info },
  actualite: { lab: "Actualité", Ico: Newspaper },
};
// Mention de fiabilité affichée dans l'en-tête d'une bulle.
const RV_FIABILITE = { officielle: "Source officielle", cabinet: "Base du cabinet", a_verifier: "À vérifier" };
const RV_INTERNE = { kb: "Base de connaissance", proc: "Procédure" };
const hote = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return url;
  }
};

// Analyse automatique des insights : après ANALYSE_MOTS mots nouveaux, pas plus
// d'une fois toutes les ANALYSE_ECART_MS, et ANALYSES_MAX fois par rendez-vous
// au plus (coût de l'IA et de la recherche web).
const ANALYSE_MOTS = 35;
const ANALYSE_ECART_MS = 25000;
const ANALYSES_MAX = 120;
const motsDe = (segs) => segs.reduce((a, s) => a + s.text.split(/\s+/).filter(Boolean).length, 0);
const BIENTOT = "Bientôt disponible : branché à la prochaine étape";
const MAIL_VALIDE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const rvNorm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const rvFmtT = (s) => String(Math.floor(s / 60)).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");
const rvEsc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const RvSvg = ({ s = 16, children, fill }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill={fill || "none"} stroke={fill ? "none" : "currentColor"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const RvMic = ({ s = 22 }) => (
  <RvSvg s={s}>
    <rect x="9" y="3" width="6" height="11" rx="3"></rect>
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"></path>
  </RvSvg>
);
const RvPause = ({ s = 20 }) => (
  <RvSvg s={s} fill="currentColor">
    <rect x="6.5" y="5" width="4" height="14" rx="1.2"></rect>
    <rect x="13.5" y="5" width="4" height="14" rx="1.2"></rect>
  </RvSvg>
);
const RvIcoMin = () => (
  <RvSvg s={15}>
    <path d="M5 18h14"></path>
  </RvSvg>
);
const RvIcoMax = () => (
  <RvSvg s={15}>
    <path d="M4 9V4h5M20 15v5h-5M15 4h5v5M9 20H4v-5"></path>
  </RvSvg>
);

const missionsDe = (c) =>
  [
    String(c.missionComptable).toLowerCase() === "oui" && "Compta",
    String(c.missionSociale).toLowerCase() === "oui" && "Social",
    String(c.missionJuridique).toLowerCase() === "oui" && "Juri",
    String(c.missionIR).toLowerCase() === "oui" && "IR",
  ].filter(Boolean);
const clotureDe = (c) => (c.dateCloture ? String(c.dateCloture).slice(5, 10).split("-").reverse().join("/") : null);

// Choix du client (liste du Portefeuille) ou du prospect (Leads, cherché au
// serveur pour le rendez-vous réel). onChange(nom, cible) : cible =
// { type: "client" | "prospect" | "libre", id, nom, email }.
function RvClientPick({ value, onChange, clients, avecProspects }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [prospects, setProspects] = useState([]);
  const list = useMemo(() => {
    const n = rvNorm(q);
    return clients.filter((c) => c.nom && (!n || rvNorm(c.nom).includes(n))).slice(0, 8);
  }, [q, clients]);
  useEffect(() => {
    const t = q.trim();
    if (!avecProspects || t.length < 2) {
      setProspects([]);
      return undefined;
    }
    let actif = true;
    const minuteur = setTimeout(() => {
      chercherProspects(t)
        .then((l) => actif && setProspects(Array.isArray(l) ? l.slice(0, 5) : []))
        .catch(() => actif && setProspects([]));
    }, 250);
    return () => {
      actif = false;
      clearTimeout(minuteur);
    };
  }, [q, avecProspects]);
  const pick = (nom, cible) => {
    onChange(nom, cible);
    setQ(nom);
    setOpen(false);
  };
  const deClient = (c) => ({ type: "client", id: c.id, nom: c.nom, email: c.email || "" });
  const deProspect = (p) => ({ type: "prospect", id: p.id, nom: p.nom, email: p.email || "" });
  const libre = (t) => ({ type: "libre", id: null, nom: t, email: "" });
  return (
    <div className="rv-pick">
      <input
        id="rv-client"
        className="rv-in"
        value={q}
        placeholder="Client ou prospect…"
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQ(e.target.value);
          onChange("", null);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          const t = q.trim();
          if (e.key !== "Enter" || !t) return;
          if (list[0]) pick(list[0].nom, deClient(list[0]));
          else if (prospects[0]) pick(prospects[0].nom, deProspect(prospects[0]));
          else pick(t, libre(t));
        }}
      />
      {open && (list.length > 0 || prospects.length > 0 || q.trim()) && (
        <div className="rv-pick-pop">
          {list.map((c) => (
            <button key={"c" + c.id} onMouseDown={() => pick(c.nom, deClient(c))}>
              <Folder size={13} /> {c.nom}
            </button>
          ))}
          {prospects.map((p) => (
            <button key={"p" + p.id} onMouseDown={() => pick(p.nom, deProspect(p))} title={p.personne || p.nom}>
              <UserRound size={13} /> {p.nom} · prospect
            </button>
          ))}
          {q.trim() && !list.some((c) => c.nom === q.trim()) && !prospects.some((p) => p.nom === q.trim()) && (
            <button className="rv-pick-new" onMouseDown={() => pick(q.trim(), libre(q.trim()))}>
              <Plus size={13} /> Prospect « {q.trim()} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Réacteur HUD : spectre radial + anneaux (animation au repos, ou « en
// écoute » pendant la démo — le micro sera branché à l'étape suivante).
function RvReactor({ levelRef, listening, busy, onToggle, size = 200 }) {
  const cv = useRef(null);
  useEffect(() => {
    let raf;
    let t = 0;
    const N = 96;
    const h = new Array(N).fill(0);
    const draw = () => {
      const c = cv.current;
      if (!c || !c.clientWidth) {
        raf = requestAnimationFrame(draw);
        return;
      }
      const dpr = window.devicePixelRatio || 1;
      const S = c.clientWidth;
      if (c.width !== S * dpr) {
        c.width = S * dpr;
        c.height = S * dpr;
      }
      const x = c.getContext("2d");
      x.setTransform(dpr, 0, 0, dpr, 0, 0);
      x.clearRect(0, 0, S, S);
      t += 0.03;
      const { bins, live } = levelRef.current;
      const cx = S / 2;
      const r0 = S * 0.3;
      const len = S * 0.17;
      x.lineCap = "round";
      x.lineWidth = Math.max(1.2, S / 150);
      for (let i = 0; i < N; i++) {
        const k = bins ? Math.floor(((i < N / 2 ? i : N - i) / (N / 2)) * bins.length * 0.75) : 0;
        const target = live ? (bins ? bins[k] / 255 : 0.3 + 0.25 * Math.sin(t * 4 + i * 0.6)) : 0.06 + 0.04 * Math.sin(t * 1.5 + i * 0.4);
        h[i] += (target - h[i]) * 0.22;
        const a = (i / N) * Math.PI * 2 - Math.PI / 2;
        const l = 2 + h[i] * len;
        x.strokeStyle = `rgba(134,220,235,${live ? 0.25 + h[i] * 0.6 : 0.18})`;
        x.beginPath();
        x.moveTo(cx + Math.cos(a) * r0, cx + Math.sin(a) * r0);
        x.lineTo(cx + Math.cos(a) * (r0 + l), cx + Math.sin(a) * (r0 + l));
        x.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [levelRef]);
  return (
    <div className={`rv-reac ${listening ? "on" : ""} ${busy ? "busy" : ""}`} style={{ "--rs": size + "px" }}>
      <svg className="rv-reac-rings" viewBox="0 0 200 200" aria-hidden="true">
        <circle className="r1" cx="100" cy="100" r="96"></circle>
        <circle className="r2" cx="100" cy="100" r="88"></circle>
        <g className="r3">
          <path d="M100 18a82 82 0 0 1 71 41"></path>
          <path d="M100 182a82 82 0 0 1-71-41"></path>
        </g>
        <circle className="r4" cx="100" cy="100" r="56"></circle>
      </svg>
      <canvas ref={cv}></canvas>
      <button className="rv-reac-core" onClick={onToggle} title={listening ? "Mettre en pause" : "Démarrer l'écoute"}>
        {listening ? <RvPause s={size > 150 ? 22 : 16} /> : <RvMic s={size > 150 ? 24 : 18} />}
      </button>
    </div>
  );
}

function RvBubble({ b, onPin, onInsert, onDrop }) {
  const T = RV_TYPES[b.type] || RV_TYPES.regle;
  return (
    <div className={`rv-bub t-${b.type} ${b.pinned ? "pinned" : ""} ${b.fresh ? "fresh" : ""}`}>
      <div className="rv-bub-h">
        <span className="rv-bub-ico">
          <T.Ico size={12} />
        </span>
        <span className="rv-bub-k">{T.lab}</span>
        {RV_FIABILITE[b.fiabilite] && <span className={`rv-bub-f ${b.fiabilite === "a_verifier" ? "a-verifier" : ""}`}>{RV_FIABILITE[b.fiabilite]}</span>}
        <span className="rv-bub-t">T+{rvFmtT(b.at)}</span>
      </div>
      <div className="rv-bub-ti">{b.titre}</div>
      <div className="rv-bub-tx">{b.texte}</div>
      {Array.isArray(b.sources) && b.sources.length > 0 && (
        <div className="rv-bub-src">
          {b.sources.map((src, i) =>
            src.url ? (
              <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" title={src.titre}>
                <Link2 size={11} /> {hote(src.url)} · {src.titre}
              </a>
            ) : (
              <span key={i} title={src.titre}>
                <BookOpen size={11} /> {RV_INTERNE[src.interne] || "Cabinet"} · {src.titre}
              </span>
            ),
          )}
        </div>
      )}
      <div className="rv-bub-a">
        <button onClick={onPin} className={b.pinned ? "on" : ""}>
          <Star size={11} /> {b.pinned ? "Épinglée" : "Épingler"}
        </button>
        <button onClick={onInsert}>
          <Pencil size={11} /> Notes
        </button>
        <button onClick={onDrop} className="rv-bub-x" title="Masquer">
          <X size={11} />
        </button>
      </div>
    </div>
  );
}

export default function RdvHud({ onClose, initialClient = "", initialClientId = null, onEnregistre }) {
  const { utilisateur } = useEtatGlobal();
  const reel = estTesteur(utilisateur);
  const { data: clientsData } = useClients();
  const clients = useMemo(() => (Array.isArray(clientsData) ? clientsData : []), [clientsData]);
  const meName = [utilisateur?.name, utilisateur?.lastName].filter(Boolean).join(" ") || "Votre conseiller";

  const [mode, setMode] = useState("float");
  const [tab, setTab] = useState("flux");
  const [phase, setPhase] = useState("live");
  const [client, setClient] = useState(initialClient);
  // Dossier auquel la note sera rattachée (voir RvClientPick).
  const [cible, setCible] = useState(initialClientId ? { type: "client", id: initialClientId, nom: initialClient, email: "" } : null);
  const [title, setTitle] = useState("");
  const [conf, setConf] = useState("public");
  const prive = conf !== "public";
  const [fichier, setFichier] = useState(null);
  const [segs, setSegs] = useState([]);
  const [interim, setInterim] = useState("");
  const [spk, setSpk] = useState("cpt");
  const [listening, setListening] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [bubbles, setBubbles] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [manual, setManual] = useState("");
  const [recap, setRecap] = useState(null);
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapErr, setRecapErr] = useState(null);
  const [micErr, setMicErr] = useState(null);
  const [saving, setSaving] = useState(null); // null, ou progression de l'envoi du document
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [toast, setToast] = useState(null);
  const [railOpen, setRailOpen] = useState(true);
  const [pickKey, setPickKey] = useState(0);

  const spkRef = useRef(spk);
  spkRef.current = spk;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const railRef = useRef(railOpen);
  railRef.current = railOpen;
  const elapsedRef = useRef(0);
  elapsedRef.current = elapsed;
  const levelRef = useRef({ bins: null, live: false });
  const notesRef = useRef(null);
  const notesHtml = useRef("");
  const trRef = useRef(null);
  const fileRef = useRef(null);
  const demoRef = useRef({ on: false, used: false });
  const toastTimer = useRef(null);
  const recRef = useRef(null); // dictée du navigateur en cours
  const wantRef = useRef(false); // écoute voulue : relance la dictée quand le navigateur la coupe
  const audioRef = useRef(null); // micro ouvert pour l'animation du réacteur
  const consentRef = useRef(false);
  const segsRef = useRef(segs);
  segsRef.current = segs;
  const cibleRef = useRef(cible);
  cibleRef.current = cible;
  // Analyse des insights en cours : sujets déjà proposés (même masqués, pour
  // ne pas les reproposer), mots au dernier envoi, nombre d'analyses.
  const analyseRef = useRef({ busy: false, mots: 0, le: 0, n: 0, sujets: [], ctrl: null, coupe: false });
  const analyserRef = useRef(null);
  const reinitAnalyse = () => {
    const a = analyseRef.current;
    if (a.ctrl) a.ctrl.abort();
    analyseRef.current = { busy: false, mots: 0, le: 0, n: 0, sujets: [], ctrl: null, coupe: false };
    setAnalyzing(false);
  };

  const flash = (m) => {
    setToast(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };
  const bientot = () => flash(BIENTOT);

  useEffect(() => {
    if (!listening) return undefined;
    const i = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(i);
  }, [listening]);
  useEffect(() => {
    if (trRef.current) trRef.current.scrollTop = trRef.current.scrollHeight;
  }, [segs, interim, tab]);
  useEffect(() => {
    if (phase === "live" && notesRef.current) notesRef.current.innerHTML = notesHtml.current;
  }, [phase]);
  useEffect(() => {
    if (railOpen && mode === "float") setUnseen(0);
  }, [railOpen, mode]);
  useEffect(
    () => () => {
      demoRef.current.on = false;
      clearTimeout(toastTimer.current);
      if (analyseRef.current.ctrl) analyseRef.current.ctrl.abort();
      wantRef.current = false;
      try {
        if (recRef.current) recRef.current.stop();
      } catch (e) {}
      const a = audioRef.current;
      audioRef.current = null;
      if (a) {
        cancelAnimationFrame(a.raf);
        a.st.getTracks().forEach((t) => t.stop());
        a.ctx.close().catch(() => {});
      }
    },
    [],
  );

  const addSeg = (text) =>
    setSegs((s) => {
      const sp = spkRef.current;
      const last = s[s.length - 1];
      if (last && last.spk === sp) return [...s.slice(0, -1), { ...last, text: last.text + " " + text }];
      return [...s, { id: Date.now() + Math.random(), spk: sp, text, at: elapsedRef.current }];
    });

  const pushNotifs = (add) => {
    if (modeRef.current === "min") {
      setNotifs((n) => [...add, ...n].slice(0, 3));
      add.forEach((b) => setTimeout(() => setNotifs((n) => n.filter((x) => x.id !== b.id)), 14000));
    }
    if (!railRef.current || modeRef.current === "min") setUnseen((u) => u + add.length);
  };
  const addBubbles = (list) => {
    const add = list.map((x, i) => ({ ...x, id: Date.now() + i + Math.random(), at: elapsedRef.current, fresh: true }));
    setBubbles((bs) => [...add, ...bs.map((b) => ({ ...b, fresh: false }))]);
    pushNotifs(add);
  };

  const stopDemo = () => {
    demoRef.current.on = false;
    levelRef.current.live = false;
    setListening(false);
    setInterim("");
  };

  const runDemo = async () => {
    if (segs.length && !demoRef.current.used && !(await dialog.confirm("Remplacer le rendez-vous en cours par la démo ? La transcription et les notes seront effacées.", { title: "Lancer la démo", variant: "danger", confirmLabel: "Remplacer" })))
      return;
    stopDemo();
    stopMic();
    reinitAnalyse();
    setPhase("live");
    setRecap(null);
    setRecapErr(null);
    setSaved(false);
    setSent(null);
    setFichier(null);
    setSegs([]);
    setBubbles([]);
    setElapsed(0);
    setUnseen(0);
    notesHtml.current = "";
    if (notesRef.current) notesRef.current.innerHTML = "";
    setClient(RV_DEMO.client);
    setCible(null);
    setTitle(RV_DEMO.title);
    setPickKey((k) => k + 1);
    const c = clients.find((x) => x.nom === RV_DEMO.client) || {};
    const missions = missionsDe(c);
    const dossier = `Clôture au ${clotureDe(c) || "31/12"}. Missions suivies : ${missions.join(", ") || "—"}.${c.expertComptable ? " Expert-comptable : " + c.expertComptable + "." : ""} Régime de TVA non renseigné dans le dossier.`;
    demoRef.current = { on: true, used: true };
    levelRef.current.live = true;
    levelRef.current.bins = null;
    setListening(true);
    await sleep(900);
    for (const l of RV_DEMO.lines) {
      if (!demoRef.current.on) return;
      spkRef.current = l.spk;
      setSpk(l.spk);
      const w = l.text.split(" ");
      for (let i = 1; i <= w.length; i++) {
        if (!demoRef.current.on) return;
        setInterim(w.slice(0, i).join(" "));
        await sleep(55 + Math.random() * 60);
      }
      setInterim("");
      addSeg(l.text);
      if (l.bubbles) {
        setAnalyzing(true);
        await sleep(1100);
        setAnalyzing(false);
        if (!demoRef.current.on) return;
        addBubbles(l.bubbles.map((b) => (b.texte === "@dossier" ? { ...b, texte: dossier } : b)));
      }
      await sleep(700);
    }
    stopDemo();
    flash("Démo terminée · cliquez sur « Terminer et synthétiser »");
  };

  // Micro : dictée du navigateur (Chrome, Edge), relancée quand le navigateur
  // la coupe (silence, réseau). L'audio n'est ni enregistré ni envoyé au
  // serveur : seul le texte transcrit est gardé, dans la note.
  const stopMic = () => {
    wantRef.current = false;
    const rec = recRef.current;
    recRef.current = null;
    try {
      if (rec) rec.stop();
    } catch (e) {}
    const a = audioRef.current;
    audioRef.current = null;
    if (a) {
      cancelAnimationFrame(a.raf);
      a.st.getTracks().forEach((t) => t.stop());
      a.ctx.close().catch(() => {});
    }
    levelRef.current.live = false;
    levelRef.current.bins = null;
    setListening(false);
    setInterim("");
  };
  // Spectre du réacteur : niveau réel du micro (l'animation reste sans lui).
  const startAudio = async () => {
    try {
      const st = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!wantRef.current) {
        st.getTracks().forEach((t) => t.stop());
        return;
      }
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const an = ctx.createAnalyser();
      an.fftSize = 128;
      an.smoothingTimeConstant = 0.7;
      ctx.createMediaStreamSource(st).connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const a = { st, ctx, raf: 0 };
      audioRef.current = a;
      const loop = () => {
        if (audioRef.current !== a) return;
        an.getByteFrequencyData(buf);
        levelRef.current.bins = buf;
        a.raf = requestAnimationFrame(loop);
      };
      loop();
    } catch (e) {
      levelRef.current.bins = null;
    }
  };
  const startMic = async () => {
    setMicErr(null);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setMicErr("Dictée vocale indisponible dans ce navigateur (Chrome ou Edge requis). Saisissez les échanges à la main ci-dessous.");
      return;
    }
    if (!consentRef.current) {
      const ok = await dialog.confirm(
        "Le client est-il informé que l'échange est transcrit pour la prise de notes ? L'audio n'est pas enregistré : seul le texte transcrit est conservé, dans la note du rendez-vous.",
        { title: "Démarrer l'écoute", confirmLabel: "Oui, démarrer" },
      );
      if (!ok) return;
      consentRef.current = true;
    }
    if (demoRef.current.used) {
      // Le texte de la démo est fictif : il laisse la place au vrai rendez-vous.
      demoRef.current.used = false;
      reinitAnalyse();
      setSegs([]);
      setBubbles([]);
      setElapsed(0);
      setUnseen(0);
      setRecap(null);
      notesHtml.current = "";
      if (notesRef.current) notesRef.current.innerHTML = "";
    }
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let fin = "";
      let int = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) fin += r[0].transcript;
        else int += r[0].transcript;
      }
      if (fin.trim()) addSeg(fin.trim());
      setInterim(int);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed" || e.error === "audio-capture") {
        setMicErr("Micro inaccessible. Autorisez le micro pour ce site dans le navigateur, ou saisissez les échanges à la main ci-dessous.");
        stopMic();
      } else if (e.error === "network") {
        setMicErr("Dictée coupée par le réseau : elle reprend automatiquement dès que la connexion revient.");
      }
    };
    rec.onend = () => {
      if (wantRef.current && recRef.current === rec) {
        try {
          rec.start();
        } catch (err) {}
      }
    };
    rec.onstart = () => setMicErr(null);
    recRef.current = rec;
    wantRef.current = true;
    try {
      rec.start();
    } catch (e) {}
    levelRef.current.live = true;
    setListening(true);
    startAudio();
  };
  const toggle = () => {
    if (demoRef.current.on) return stopDemo();
    if (!reel) return bientot();
    return listening ? stopMic() : startMic();
  };

  // Insights en direct : la fin de la conversation part au serveur, qui renvoie
  // les bulles une à une (dossier et questions d'abord, règles et actualité
  // après leur recherche). manuel : bouton « Analyser maintenant ».
  const analyser = async (manuel) => {
    const a = analyseRef.current;
    const conv = segsRef.current;
    if (!reel || demoRef.current.used || a.busy || a.coupe || !conv.length) return;
    if (a.n >= ANALYSES_MAX) {
      if (manuel) flash("Limite d'analyses atteinte pour ce rendez-vous");
      return;
    }
    const ctrl = new AbortController();
    Object.assign(a, { busy: true, n: a.n + 1, le: Date.now(), mots: motsDe(conv), ctrl });
    setAnalyzing(true);
    const c = cibleRef.current;
    try {
      await analyserInsights(
        {
          clientId: c && c.type === "client" ? c.id : null,
          formLeadId: c && c.type === "prospect" ? c.id : null,
          conversation: conv.slice(-40).map(({ spk: k, text }) => ({ spk: k, text })),
          dejaVus: a.sujets.slice(-80),
        },
        (e) => {
          if (analyseRef.current !== a) return; // rendez-vous vidé entre-temps
          if (e.t === "insight" && e.b && e.b.titre) {
            a.sujets.push(e.b.sujet || e.b.titre);
            addBubbles([e.b]);
          } else if (e.t === "erreur" && manuel) flash(e.message);
        },
        ctrl.signal,
      );
    } catch (e) {
      if (e.name !== "AbortError") {
        if (e.status === 429) {
          a.coupe = true;
          flash("Quota IA du jour atteint : les insights sont en pause");
        } else if (manuel) flash(e.message || "Analyse indisponible pour le moment");
      }
    } finally {
      if (a.ctrl === ctrl) {
        a.busy = false;
        a.ctrl = null;
        if (analyseRef.current === a) setAnalyzing(false);
      }
    }
  };
  analyserRef.current = analyser;

  // Analyse automatique pendant l'échange (rendez-vous réel seulement).
  useEffect(() => {
    if (!reel || phase !== "live") return undefined;
    const minuteur = setInterval(() => {
      const a = analyseRef.current;
      if (a.busy || a.coupe || demoRef.current.used) return;
      if (motsDe(segsRef.current) - a.mots >= ANALYSE_MOTS && Date.now() - a.le >= ANALYSE_ECART_MS) analyserRef.current(false);
    }, 4000);
    return () => clearInterval(minuteur);
  }, [reel, phase]);

  const words = segs.reduce((a, s) => a + s.text.split(/\s+/).length, 0);
  const pin = (id) => setBubbles((l) => l.map((x) => (x.id === id ? { ...x, pinned: !x.pinned } : x)));
  const insertNote = (b) => {
    const el = notesRef.current;
    const liens = (b.sources || []).filter((x) => x.url).map((x) => `<a href="${rvEsc(x.url)}" target="_blank" rel="noopener noreferrer">${rvEsc(hote(x.url))}</a>`);
    const html = `<p><b>${rvEsc(b.titre)}</b> — ${rvEsc(b.texte)}${liens.length ? ` (sources : ${liens.join(", ")})` : ""}</p>`;
    if (el) {
      el.insertAdjacentHTML("beforeend", html);
      notesHtml.current = el.innerHTML;
    } else notesHtml.current += html;
    flash("Ajouté aux notes manuelles");
  };
  const cmd = (c) => {
    document.execCommand(c, false);
    if (notesRef.current) {
      notesRef.current.focus();
      notesHtml.current = notesRef.current.innerHTML;
    }
  };
  const notesText = () => {
    const d = document.createElement("div");
    d.innerHTML = notesHtml.current;
    return (d.innerText || d.textContent || "").trim();
  };
  const clearAll = async () => {
    if (!segs.length && !notesText() && !bubbles.length) return;
    if (!(await dialog.confirm("Effacer la transcription, les notes et les bulles ?", { title: "Vider le rendez-vous", variant: "danger", confirmLabel: "Effacer" }))) return;
    stopDemo();
    stopMic();
    reinitAnalyse();
    demoRef.current.used = false;
    setSegs([]);
    setBubbles([]);
    setElapsed(0);
    setUnseen(0);
    notesHtml.current = "";
    if (notesRef.current) notesRef.current.innerHTML = "";
  };

  const canRecap = segs.length > 0 || notesText().length > 0;
  const transcriptionTexte = () => segs.map((s) => `${RV_SPK[s.spk]} : ${s.text}`).join("\n");
  const epinglees = () => bubbles.filter((b) => b.pinned).map(({ type, titre, texte, sources }) => ({ type, titre, texte, sources: sources || [] }));
  // Adresse du client ou du prospect, pour préremplir le mail.
  const emailCible = () => {
    if (!cible) return "";
    if (cible.email) return cible.email;
    const c = cible.type === "client" ? clients.find((x) => x.id === cible.id) : null;
    return (c && c.email) || "";
  };

  // Synthèse : rejouée pour la démo ; générée par l'IA (serveur) sinon.
  const genRecap = async () => {
    if (!demoRef.current.used && !reel) {
      bientot();
      return;
    }
    stopDemo();
    stopMic();
    if (analyseRef.current.ctrl) analyseRef.current.ctrl.abort();
    setPhase("recap");
    setRecapBusy(true);
    setRecapErr(null);
    setSaved(false);
    setSent(null);
    if (mode === "min") setMode("float");
    if (demoRef.current.used) {
      await sleep(1400);
      const R = RV_DEMO.recap;
      setRecap({ synthese: R.synthese, actions: R.actions.map((a, i) => ({ id: i, ...a, on: true })), to: "", objet: R.objet, corps: R.corps.replace("{me}", meName) });
      setRecapBusy(false);
      return;
    }
    try {
      const r = await synthetiser({
        client,
        titre: title,
        duree: elapsedRef.current,
        transcription: segs.map(({ spk: k, text }) => ({ spk: k, text })),
        notes: notesText(),
        epingles: epinglees(),
      });
      setRecap((avant) => ({
        synthese: r.synthese,
        actions: (r.actions || []).map((a, i) => ({ id: i, texte: a.texte, qui: a.qui, echeance: a.echeance || "", on: true })),
        to: (avant && avant.to) || emailCible(),
        objet: (r.mail && r.mail.objet) || `Récapitulatif de notre rendez-vous${title.trim() ? " — " + title.trim() : ""}`,
        corps: (r.mail && r.mail.corps) || "",
      }));
    } catch (e) {
      setRecapErr(e.message || "La synthèse n'a pas pu être générée. Réessayez.");
    } finally {
      setRecapBusy(false);
    }
  };
  const updA = (id, p) => setRecap((r) => ({ ...r, actions: r.actions.map((a) => (a.id === id ? { ...a, ...p } : a)) }));

  const save = async () => {
    if (demoRef.current.used) {
      flash("Démo : rien n'est enregistré");
      return;
    }
    if (!reel) {
      bientot();
      return;
    }
    if (!cible || cible.type === "libre") {
      flash(cible ? `« ${cible.nom} » n'est ni un client ni un prospect enregistré : créez-le dans Leads, ou choisissez un dossier existant` : "Choisissez le client ou le prospect du rendez-vous");
      return;
    }
    setSaving(0);
    try {
      await enregistrerNote({
        cible,
        auteur: meName,
        titre: title.trim() || "Rendez-vous",
        conf,
        transcription: transcriptionTexte(),
        notesHtml: notesHtml.current,
        synthese: recap.synthese.trim(),
        actions: recap.actions.filter((a) => a.on && a.texte.trim()).map((a) => ({ texte: a.texte.trim(), qui: a.qui, echeance: a.echeance.trim() })),
        insights: epinglees(),
        duree: elapsedRef.current,
        fichier,
        onProgression: setSaving,
      });
      setSaved(true);
      flash(`Note enregistrée dans la fiche ${cible.nom}`);
      if (onEnregistre) onEnregistre();
    } catch (e) {
      flash(e.message || "Enregistrement impossible. Réessayez.");
    } finally {
      setSaving(null);
    }
  };

  // Envoi depuis la boîte Gmail du collaborateur ; sinon « Ma messagerie ».
  const send = async () => {
    const to = recap.to.trim();
    if (!MAIL_VALIDE.test(to)) {
      flash("Adresse e-mail du client invalide");
      return;
    }
    if (!recap.corps.trim()) {
      flash("Le mail est vide");
      return;
    }
    const aCompleter = /\[À COMPLÉTER\]/i.test(recap.objet + recap.corps);
    const ok = await dialog.confirm(
      `${aCompleter ? "Le mail contient encore « [À COMPLÉTER] ». " : ""}Envoyer le récapitulatif à ${to} depuis votre boîte Gmail ?`,
      { title: "Envoyer le mail", variant: aCompleter ? "danger" : undefined, confirmLabel: "Envoyer" },
    );
    if (!ok) return;
    setSending(true);
    try {
      await envoyerMail({ to, subject: recap.objet.trim() || "Récapitulatif de notre rendez-vous", body: recap.corps });
      setSent(to);
      flash("Récapitulatif envoyé à " + to);
    } catch (e) {
      flash(e.status === 404 ? "Votre boîte Gmail n'est pas connectée à MyALFA (écran Mails) : utilisez « Ma messagerie »" : e.message);
    } finally {
      setSending(false);
    }
  };

  const close = async () => {
    if ((segs.length || recap) && !saved && !(await dialog.confirm("Fermer sans enregistrer la synthèse ?", { title: "Fermer le rendez-vous", confirmLabel: "Fermer" }))) return;
    stopDemo();
    stopMic();
    onClose();
  };

  const ci = cible && cible.type === "client" ? clients.find((c) => c.id === cible.id) : cible ? null : clients.find((c) => c.nom === client);
  const statusLab = analyzing ? "Analyse" : listening ? "Écoute" : segs.length ? "Pause" : "Veille";

  const mini = (
    <div className={`rv-mini-wrap ${mode === "min" ? "" : "hide"}`}>
      <div className="rv-notifs">
        {notifs.map((b) => {
          const T = RV_TYPES[b.type] || RV_TYPES.regle;
          return (
            <div key={b.id} className={`rv-notif t-${b.type}`}>
              <div className="rv-bub-h">
                <span className="rv-bub-ico">
                  <T.Ico size={12} />
                </span>
                <span className="rv-bub-k">{T.lab}</span>
                <button className="rv-notif-x" title="Masquer" onClick={() => setNotifs((n) => n.filter((x) => x.id !== b.id))}>
                  <X size={11} />
                </button>
              </div>
              <div className="rv-bub-ti">{b.titre}</div>
              <div className="rv-bub-tx">{b.texte}</div>
              <div className="rv-bub-a">
                <button
                  onClick={() => {
                    pin(b.id);
                    setNotifs((n) => n.filter((x) => x.id !== b.id));
                  }}
                >
                  <Star size={11} /> Épingler
                </button>
                <button
                  onClick={() => {
                    setMode("float");
                    setRailOpen(true);
                  }}
                >
                  <RvIcoMax /> Ouvrir
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className={`rv-mini ${listening ? "on" : ""}`}>
        <RvReactor levelRef={levelRef} listening={listening} busy={analyzing} onToggle={toggle} size={58} />
        <div className="rv-mini-t" onClick={() => setMode("float")}>
          <b>{client || "Rendez-vous"}</b>
          <small>
            <span className="rv-dot"></span>
            {statusLab} · <span className="mono">{rvFmtT(elapsed)}</span>
            {bubbles.length ? ` · ${bubbles.length} insight${bubbles.length > 1 ? "s" : ""}` : ""}
          </small>
        </div>
        {unseen > 0 && <span className="rv-badge">{unseen}</span>}
        <button className="rv-hb" title="Agrandir" onClick={() => setMode("float")}>
          <RvIcoMax />
        </button>
      </div>
    </div>
  );

  const rail = mode === "float" && phase === "live" && (
    <div className={`rv-rail side-left ${railOpen ? "" : "closed"}`} style={{ width: 330 }}>
      <div className="rv-rail-h">
        <span className={`rv-orb ${analyzing ? "busy" : listening ? "on" : ""}`}></span>
        <div className="rv-rail-ht">
          <b>Insights{bubbles.length ? ` · ${bubbles.length}` : ""}</b>
          <small>{analyzing ? "Analyse du contexte…" : listening ? "Suit la conversation" : "En attente"}</small>
        </div>
        {!railOpen && unseen > 0 && <span className="rv-badge sm">{unseen}</span>}
        {railOpen && segs.length > 0 && !demoRef.current.used && (
          <button className="rv-hb" title="Analyser maintenant" disabled={analyzing} onClick={reel ? () => analyser(true) : bientot}>
            <Sparkles size={13} />
          </button>
        )}
        <button className="rv-hb" title={railOpen ? "Masquer les insights" : "Afficher les insights"} onClick={() => setRailOpen(!railOpen)}>
          {railOpen ? <RvIcoMin /> : <RvIcoMax />}
        </button>
      </div>
      {railOpen && (
        <div className="rv-rail-list">
          {analyzing && (
            <div className="rv-bub rv-bub-sk">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          {bubbles.map((b) => (
            <RvBubble key={b.id} b={b} onPin={() => pin(b.id)} onInsert={() => insertNote(b)} onDrop={() => setBubbles((l) => l.filter((x) => x.id !== b.id))} />
          ))}
          {client && (
            <div className="rv-bub rv-ctx">
              <div className="rv-ctx-n">
                <Folder size={13} /> {client}
              </div>
              {ci ? (
                <div className="rv-ctx-g">
                  <span>
                    Clôture <b>{clotureDe(ci) || "—"}</b>
                  </span>
                  <span>
                    Missions <b>{missionsDe(ci).join(" · ") || "—"}</b>
                  </span>
                  {ci.expertComptable && (
                    <span>
                      EC <b>{ci.expertComptable}</b>
                    </span>
                  )}
                </div>
              ) : (
                <div className="rv-ctx-g">
                  <span>Prospect · pas encore de dossier</span>
                </div>
              )}
            </div>
          )}
          {!bubbles.length && !analyzing && (
            <div className="rv-bub rv-bubs-empty">
              <Sparkles size={16} />
              <p>Les règles et seuils, l'actualité évoquée, les infos du dossier et les questions à poser apparaîtront ici au fil de la conversation.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return createPortal(
    <div className="rvh">
      {mini}
      {rail}
      <div className={`rv rv-${mode} ${listening ? "is-live" : ""}`} data-tab={tab} role="dialog" aria-label="Rendez-vous assisté par IA">
        <div className="rv-head">
          <span className={`rv-dot ${listening ? "on" : ""}`}></span>
          <div className="rv-head-t">
            <b>Rendez-vous</b>
            <small>
              {statusLab} <span className="mono">{rvFmtT(elapsed)}</span>
            </small>
          </div>
          <div className="rv-modes">
            <button title="Rejouer la démo" onClick={runDemo}>
              <RotateCcw size={14} />
            </button>
            <button title="Réduire · les insights arrivent en notification" onClick={() => setMode("min")}>
              <RvIcoMin />
            </button>
            <button onClick={close} title="Fermer">
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="rv-meta">
          <RvClientPick
            key={pickKey}
            value={client}
            onChange={(nom, c) => {
              setClient(nom);
              setCible(c);
            }}
            clients={clients}
            avecProspects={reel}
          />
          <div className="rv-meta-row">
            <input id="rv-titre" className="rv-in" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre · ex. Point TVA 2026" />
            <select id="rv-conf" className="rv-in rv-sel" value={conf} onChange={(e) => setConf(e.target.value)} title="Confidentialité">
              <option value="public">Public</option>
              <option value="prive">Privé</option>
              <option value="associe">Associé</option>
            </select>
            <input id="rv-fichier" ref={fileRef} type="file" hidden accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md" onChange={(e) => setFichier(e.target.files[0] || null)} />
            <button
              className={`rv-hb sq ${fichier ? "on" : ""}`}
              title={fichier ? fichier.name + " (cliquer pour retirer)" : "Joindre un document à la note (optionnel)"}
              onClick={() => {
                if (fichier) {
                  setFichier(null);
                  fileRef.current.value = "";
                } else fileRef.current.click();
              }}
            >
              <FileText size={14} />
            </button>
          </div>
          {(prive || fichier) && (
            <div className="rv-meta-info">
              {conf === "prive" && (
                <span>
                  <Lock size={11} /> Note visible par vous, les administrateurs et les experts-comptables
                </span>
              )}
              {conf === "associe" && (
                <span>
                  <Users size={11} /> Note visible par vous, les administrateurs et les experts-comptables
                </span>
              )}
              {fichier && (
                <span>
                  <FileText size={11} /> {fichier.name}
                </span>
              )}
            </div>
          )}
        </div>

        {phase === "live" ? (
          <>
            <div className="rv-tabs">
              {[
                ["flux", "Transcription"],
                ["notes", "Notes"],
              ].map(([k, l]) => (
                <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>
                  {l}
                </button>
              ))}
            </div>
            <div className="rv-body">
              <div className="rv-colL">
                <div className="rv-stage">
                  <RvReactor levelRef={levelRef} listening={listening} busy={analyzing} onToggle={toggle} size={168} />
                  <div className="rv-stage-side">
                    {!listening && !segs.length && (
                      <button className="rv-demo" onClick={runDemo}>
                        <Sparkles size={13} /> Lancer la démo
                      </button>
                    )}
                    <div className="rv-stage-st">
                      <span className="lab">Statut</span>
                      <b>{listening ? "Écoute active" : segs.length ? "En pause" : "Prêt"}</b>
                    </div>
                    <div className="rv-stage-st">
                      <span className="lab">Durée</span>
                      <b className="mono">{rvFmtT(elapsed)}</b>
                    </div>
                    <div className="rv-spk">
                      <span className="lab">Qui parle</span>
                      <div>
                        {Object.entries(RV_SPK).map(([k, l]) => (
                          <button key={k} className={spk === k ? "on k-" + k : ""} onClick={() => setSpk(k)}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {micErr && (
                  <div className="rv-err" role="alert">
                    <AlertTriangle size={14} /> {micErr}
                  </div>
                )}

                <div className="rv-pane p-flux">
                  <div className="rv-sec-h">
                    <span>Transcription</span>
                    <small>par {meName}</small>
                  </div>
                  <div className="rv-tr" ref={trRef}>
                    {segs.length === 0 && !interim && <div className="rv-tr-empty">En attente du signal audio…</div>}
                    {segs.map((s) => (
                      <div key={s.id} className={`rv-seg-l k-${s.spk}`}>
                        <button
                          className="rv-seg-who"
                          title="Changer d'interlocuteur"
                          onClick={() => setSegs((l) => l.map((x) => (x.id === s.id ? { ...x, spk: x.spk === "cpt" ? "cli" : "cpt" } : x)))}
                        >
                          {RV_SPK[s.spk]}
                        </button>
                        <p>{s.text}</p>
                        <span className="rv-seg-t mono">{rvFmtT(s.at)}</span>
                      </div>
                    ))}
                    {interim && (
                      <div className={`rv-seg-l k-${spk} interim`}>
                        <span className="rv-seg-who">{RV_SPK[spk]}</span>
                        <p>{interim}</p>
                      </div>
                    )}
                  </div>
                  <div className="rv-manual">
                    <input
                      id="rv-replique"
                      value={manual}
                      onChange={(e) => setManual(e.target.value)}
                      placeholder={`Saisir une réplique · ${RV_SPK[spk]}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && manual.trim()) {
                          addSeg(manual.trim());
                          setManual("");
                        }
                      }}
                    />
                    <button
                      disabled={!manual.trim()}
                      title="Ajouter la réplique"
                      onClick={() => {
                        addSeg(manual.trim());
                        setManual("");
                      }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                <div className="rv-pane p-notes">
                  <div className="rv-sec-h">
                    <span>Notes manuelles</span>
                  </div>
                  <div className="rv-notes">
                    <div className="rv-tb">
                      <button title="Gras" onMouseDown={(e) => { e.preventDefault(); cmd("bold"); }}>
                        <b>B</b>
                      </button>
                      <button title="Italique" onMouseDown={(e) => { e.preventDefault(); cmd("italic"); }}>
                        <i>I</i>
                      </button>
                      <button title="Souligné" onMouseDown={(e) => { e.preventDefault(); cmd("underline"); }}>
                        <u>U</u>
                      </button>
                      <span></span>
                      <button title="Liste à puces" onMouseDown={(e) => { e.preventDefault(); cmd("insertUnorderedList"); }}>
                        <List size={14} />
                      </button>
                      <button title="Liste numérotée" onMouseDown={(e) => { e.preventDefault(); cmd("insertOrderedList"); }}>
                        <ListOrdered size={14} />
                      </button>
                      <button title="Effacer la mise en forme" onMouseDown={(e) => { e.preventDefault(); cmd("removeFormat"); }}>
                        <RemoveFormatting size={14} />
                      </button>
                    </div>
                    <div
                      ref={notesRef}
                      className="rv-notes-ed"
                      contentEditable
                      suppressContentEditableWarning
                      data-ph="Notes prises pendant le rendez-vous…"
                      onInput={(e) => {
                        notesHtml.current = e.currentTarget.innerHTML;
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="rv-body rv-recap">
            {recapBusy ? (
              <div className="rv-recap-load">
                <span className="rv-orb busy big"></span>
                <b>Rédaction de la synthèse…</b>
                <small>Transcription · notes manuelles · insights épinglés</small>
              </div>
            ) : recapErr ? (
              <div className="rv-recap-load">
                <div className="rv-err" role="alert">
                  <AlertTriangle size={14} /> {recapErr}
                </div>
                <button className="rv-btn" onClick={genRecap}>
                  Réessayer
                </button>
              </div>
            ) : (
              recap && (
                <>
                  <div className="rv-colL">
                    <div className="rv-sec-h">
                      <span>
                        <Sparkles size={13} /> Synthèse IA
                      </span>
                      <small>Modifiable avant enregistrement</small>
                    </div>
                    <textarea id="rv-synthese" className="rv-ta rv-ta-syn" value={recap.synthese} onChange={(e) => setRecap({ ...recap, synthese: e.target.value })}></textarea>
                    <div className="rv-sec-h">
                      <span>Points d'action</span>
                      <small>{recap.actions.filter((a) => a.on).length} retenus</small>
                    </div>
                    <div className="rv-acts">
                      {recap.actions.map((a) => (
                        <div key={a.id} className={`rv-act ${a.on ? "" : "off"}`}>
                          <button className={`rv-chk ${a.on ? "on" : ""}`} title={a.on ? "Retirer" : "Retenir"} onClick={() => updA(a.id, { on: !a.on })}>
                            {a.on && <Check size={11} />}
                          </button>
                          <input id={`rv-action-${a.id}`} value={a.texte} onChange={(e) => updA(a.id, { texte: e.target.value })} />
                          <button className={`rv-qui q-${a.qui}`} onClick={() => updA(a.id, { qui: a.qui === "Cabinet" ? "Client" : "Cabinet" })}>
                            {a.qui}
                          </button>
                          <input id={`rv-echeance-${a.id}`} className="rv-ech" value={a.echeance} placeholder="Échéance" onChange={(e) => updA(a.id, { echeance: e.target.value })} />
                        </div>
                      ))}
                      <button
                        className="rv-act-add"
                        onClick={() => setRecap({ ...recap, actions: [...recap.actions, { id: Date.now(), texte: "", qui: "Cabinet", echeance: "", on: true }] })}
                      >
                        <Plus size={13} /> Ajouter une action
                      </button>
                    </div>
                  </div>
                  <aside className="rv-colR rv-mail">
                    <div className="rv-sec-h">
                      <span>
                        <Mail size={13} /> Récapitulatif client
                      </span>
                      {sent && (
                        <small className="rv-ok">
                          <Check size={12} /> Envoyé
                        </small>
                      )}
                    </div>
                    <input id="rv-mail-a" className="rv-in" value={recap.to} onChange={(e) => setRecap({ ...recap, to: e.target.value })} placeholder="À · adresse@client.fr" />
                    <input id="rv-mail-objet" className="rv-in" value={recap.objet} onChange={(e) => setRecap({ ...recap, objet: e.target.value })} placeholder="Objet" />
                    <textarea id="rv-mail-corps" className="rv-ta rv-ta-mail" value={recap.corps} onChange={(e) => setRecap({ ...recap, corps: e.target.value })}></textarea>
                    <div className="rv-mail-a">
                      <a
                        className="rv-btn"
                        href={`mailto:${encodeURIComponent(recap.to.trim())}?subject=${encodeURIComponent(recap.objet)}&body=${encodeURIComponent(recap.corps)}`}
                        title="Ouvrir le mail dans votre messagerie"
                      >
                        <ExternalLink size={13} /> Ma messagerie
                      </a>
                      {reel && !demoRef.current.used && (
                        <button className="rv-btn hud" disabled={sending} onClick={send} title="Envoyer depuis votre boîte Gmail connectée à MyALFA">
                          <Send size={13} /> {sending ? "Envoi…" : sent ? "Renvoyer" : "Envoyer"}
                        </button>
                      )}
                    </div>
                  </aside>
                </>
              )
            )}
          </div>
        )}

        <div className="rv-foot">
          {phase === "live" ? (
            <>
              <button className="rv-btn ghost" onClick={clearAll} title="Vider">
                <Trash2 size={14} />
              </button>
              <span className="rv-foot-info mono">{segs.length ? `${words} mots · ${bubbles.length} insights` : ""}</span>
              <button className="rv-btn primary" disabled={!canRecap} onClick={genRecap}>
                <Sparkles size={14} /> Terminer et synthétiser
              </button>
            </>
          ) : (
            <>
              <button className="rv-btn ghost" onClick={() => setPhase("live")}>
                <ArrowLeft size={14} /> Retour
              </button>
              {!recapBusy && (
                <button className="rv-btn ghost" onClick={genRecap} title="Régénérer">
                  <RotateCcw size={14} />
                </button>
              )}
              <span className="rv-foot-info"></span>
              <button className="rv-btn primary" disabled={!recap || recapBusy || saved || saving !== null} onClick={save}>
                {saved ? (
                  <>
                    <Check size={14} /> Enregistré
                  </>
                ) : saving !== null ? (
                  saving > 0 ? `Envoi du document… ${saving} %` : "Enregistrement…"
                ) : (
                  <>
                    <Folder size={14} /> Enregistrer dans la fiche
                  </>
                )}
              </button>
            </>
          )}
        </div>
        {toast && (
          <div className="rv-toast" role="status">
            {toast}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

